import { useMemo } from 'react';
import styles from './styles.module.scss';
import { SliderStepperProps } from './props';

/**
 * SliderStepper Component
 * A component that renders step indicators for a slider/carousel, showing the current position
 * and allowing navigation between steps.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.currentStep - The currently active step index
 * @param {(step: number) => void} props.onStepClick - Callback function when a step is clicked
 * @param {number} props.steps - Total number of steps to display
 * @param {boolean} props.absolute - Whether to use absolute positioning for the stepper
 * @returns {JSX.Element} The rendered stepper component with step indicators
 *
 * @example
 * <SliderStepper
 *   currentStep={2}
 *   onStepClick={(stepIndex) => setCurrentStep(stepIndex)}
 *   steps={5}
 *   absolute={true}
 * />
 */
export default function SliderStepper({
  currentStep,
  onStepClick,
  steps,
  absolute,
}: SliderStepperProps) {
  /**
   * Memoized array of step indicator elements
   * Recalculates only when steps, currentStep or onStepClick changes
   */
  const Steps = useMemo(
    () =>
      Array.from({ length: steps }).map((_, index) => (
        <div
          className={`${styles.container__step} ${index == currentStep ? styles['container__step--active'] : ''}`}
          key={`slider-stepper-${index}`}
          onClick={() => onStepClick(index)}
        />
      )),
    [steps, currentStep, onStepClick],
  );

  return (
    <div
      className={`${styles.container} ${absolute ? styles['container--absolute'] : ''}`}
    >
      {Steps}
    </div>
  );
}
